//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
  PaginationState,
  VisibilityState,
  Row,
  FilterFnOption,
} from '@tanstack/react-table';
import { format, formatISO, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Double Tap Support
import { DoubleTapRow } from '../components/DoubleTapRow';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  ShowFiltersButton,
  ArrowBottomStyled,
  SubContainerHead,
  FiltersWithLoadingStyled,
  ContainerLoader,
  ContainerStyled,
} from './styles';
import {
  Th,
  Tbody,
  Td,
  FigureRed,
  FigureGreen,
  Title,
  ContainerButtons,
  Table,
  SubContainerFilters,
  TextCell,
  GreenArrowStyled,
  RedArrowStyled,
  TextHeader,
  InputRadioContainer,
  InputRadioStyled,
  InputCalendarStyled,
  SearchableDropdownStyled,
  MultiSelectDropdownStyled,
} from '../components/MainStyledComponents';
import { SortableHeader } from '../components/SortableHeader';
import isEqual from 'lodash.isequal';

// Components
import { PaginationButtons } from '../components/PaginationButtons';
import InputText from '../../../inputs/InputText';
import InputExpedient from '../../../inputs/InputExpedient';
import InputIndex from '../../../inputs/InputIndex';
import InputPK from '../../../inputs/InputPK';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { createFlagColumn, exportIgnoreProps } from '../components/flagColumn';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import IncludeInactiveToggle from '../../../inputs/IncludeInactiveToggle';

// Types
import { tableProps, dataTable, option } from './types';
import {
  Options,
  onChangeValue,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import { ImperativeRefSearchableDropdown, OptionSelected } from '../../../inputs/SearchableDropdown/types';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import {
  columnID,
  columnNames,
  getColumnNames,
  getColumnId,
} from '../components/types';
import { CommunicationSendStatus } from '../../../../types/communication';
import { Role } from '../../../../types/auth';
import { OrderState } from '../../../../types/orders';

// Mocks
import { mocksTable } from './mocks';

//Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';

// Spaces
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../../hooks/useMediaBreakPoints';

// Feature Flags
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';

// Actions
import { setCursor } from '../../../../state/actions/ui/cursorTables';

// GQL
import { client } from '../../../../gql/client';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { GET_ROADS_BY_IDS } from '../../../../gql/queries/geoPositions/roads';
import { GET_CLASSIFICATION_BY_ID_OR_ROID } from '../../../../gql/queries/orders/predefinedData';

// Utils
import {
  generateRoidPadding,
  getStateOptionsByRoleAndEntityType,
  getContainerTypeOptions,
} from '../../../../utils/order';
import { takeBase, TABLE_ENTITY_TYPES, tableIds, CONTAINER_SCOPED_TABLE_IDS, listingFiltersByRole } from '../types';
import { Loader } from '../../../inputs/Loader';
import { useServerPageSize } from '../utils/useServerPageSize';
import { useMirroredCursor } from '../utils/useMirroredCursor';
import { filterAndSortMultimediaUsers } from '../../../../utils/multimediaUserFilters';
import { SpecialTag } from '../../SpecialTag';
import { variant, size } from '../../SpecialTag/types';

// Columnas ordenables server-side → campo del backend (whitelist Communication)
const SORTABLE_COLUMNS = {
  state: 'state',
  registerInitDate: 'registerInitDate',
  creationDate: 'creationDate',
  pkInit: 'pkInitKm',
  pkEnd: 'pkEndKm',
  sendStatus: 'sendStatus',
} as const;
const DEFAULT_SORT_FIELD = 'registerInitDate';

/**
 * Por defecto la bandera marca la Order de la fila: la fila de comunicación ya
 * lleva su id (`selectCommunicationOrders` → `order.id`). Fuera del componente
 * para que la referencia sea estable y no rehaga las columnas en cada render.
 */
const defaultGetFlagOrderId = (row: dataTable): string | undefined =>
  row.id || undefined;

export const InputRadioArrowTable = ({
  titleText,
  data,
  setRowState,
  onReFetchData,
  onReFetchPagination,
  tableId,
  getDoubleTapNavigationPath,
  onRowOpen,
  // Fuera de `props`: no son atributos del <table>.
  showFlagColumn = false,
  showOnlyFlaggedFilter = false,
  getFlagOrderId = defaultGetFlagOrderId,
  ...props
}: tableProps): JSX.Element => {
  const { t } = useTranslation(['tables', 'common', 'forms']);
  const flagScopeTexts = useFlagScopeTexts();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const globalCursor = useSelector(getCursor(tableId), isEqual);
  const isLoadingData = useSelector(getIsLoadingData(tableId), isEqual);
  const dataAll = data?.ordersWithDataCommunication
    ? data?.ordersWithDataCommunication
    : mocksTable;
  const inputStateRef = useRef<ImperativeRefMultiSelect>(null);
  const inputUserRef = useRef<ImperativeRefMultiSelect>(null);
  // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen".
  const inputContainerTypeRef = useRef<ImperativeRefMultiSelect>(null);
  const inputTeamRef = useRef<ImperativeRefMultiSelect>(null);
  const inputListGroupRef = useRef<ImperativeRefInput>(null);
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputRoadRef = useRef<ImperativeRefInput>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
  const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);
  const inputListClassificationRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);
  const hasAutoSelectedConcession = useRef<boolean>(false);

  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const [showFilters, setShowFilters] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string,
  );
  const [expedientDefault, setExpedientDefault] = useState<number | undefined>(
    undefined
  );
  const [indexDefault, setIndexDefault] = useState<number | undefined>(
    undefined
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: takeBase,
  });

  const initialRoadOptions = useMemo(
    () =>
      data?.filtersData?.data?.roads?.map(
        (road: { name: string; id: string }) => ({
          label: road.name,
          value: road.id,
        })
      ),
    [data?.filtersData?.data?.roads]
  );

  const [roadOptions, setRoadOptions] = useState(initialRoadOptions);

  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    roadId: string | undefined;
    interlocutorGroupId: string | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat with other tables sharing this cursor shape)
    states: string[] | undefined; // multi-select (Plan 2 Tarea: filtro estado multi-select Communications)
    userIds: string[] | undefined; // multi-select creador (Plan 2 Tarea 15)
    containerTypes: string[] | undefined; // multi-select "Origen" (Fase 4 nav Trabajo/Revisión)
    teamIds: string[] | undefined; // multi-select equipo (heredado del expediente padre)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador" (Plan Task 8)
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    sortField: string | undefined;
    orderBy: 'asc' | 'desc' | undefined;
    onlyFlagged: true | undefined; // «Solo marcadas»: true o undefined, nunca false
  }>(globalCursor);

  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    indexType: number | undefined;
    indexExpedition: number | undefined;
    roadId: string | undefined;
    interlocutorGroupId: string | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat)
    states: string[] | undefined; // multi-select
    userIds: string[] | undefined; // multi-select creador (Plan 2 Tarea 15)
    containerTypes: string[] | undefined; // multi-select "Origen" (Fase 4 nav Trabajo/Revisión)
    teamIds: string[] | undefined; // multi-select equipo (heredado del expediente padre)
    includeInactive: boolean | undefined; // toggle "Incluir inactivos en Creador" (Plan Task 8)
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
  }>({
    description: undefined,
    indexType: undefined,
    indexExpedition: undefined,
    roadId: undefined,
    interlocutorGroupId: undefined,
    concessionId: undefined,
    fromDate: undefined,
    toDate: undefined,
    classification: undefined,
    predefinedOrderId: undefined,
    state: undefined,
    states: undefined,
    userIds: undefined,
    containerTypes: undefined,
    teamIds: undefined,
    includeInactive: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
  });

  const isLanscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  // Tarea #11 — page-size selector centralizado en `useServerPageSize`.
  // Activo en las dos tablas full-page que consumen InputRadioArrowTable: la
  // lista principal de Comunicaciones y el wizard "Duplicar comunicación"
  // (UX idéntica a la lista, no es un modal). El storage es por tableId, así
  // que cada una recuerda su propia preferencia.
  const { takePagination, paginationProps } = useServerPageSize({
    tableId,
    enabled:
      tableId === tableIds.WORKORDER_COMMUNICATIONS ||
      tableId === tableIds.DUPLICATE_COMMUNICATION,
    selectorCursor,
    liveFilters: selectorData as unknown as Record<string, unknown>,
    setCurrentServerPage,
    onReFetchData,
  });

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  // Fase 4 nav Trabajo/Revisión (2026-07-12) — la columna+filtro "Origen" es
  // redundante en las pestañas CONTEXTUALES (campaña/RV). Se deriva del
  // `tableId` (no una prop nueva) — mismo criterio que `TABLE_ENTITY_TYPES`.
  const isContainerScopedTable = CONTAINER_SCOPED_TABLE_IDS.has(
    tableId as string
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    containerType: !isContainerScopedTable,
  });

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const roleActive = data.roleActive;

  // NOTE: Communications does NOT apply default state filter
  // All communications should be visible without state filtering
  // @see BUG #9 in TRACKING_BUGS_TESTING_2026.md

  const callbackSetRoid = useCallback(async () => {
    let classification;
    if (selectorCursor?.classification) {
      const classificationRaw = await client.query({
        query: GET_CLASSIFICATION_BY_ID_OR_ROID,
        variables: {
          id: selectorCursor?.classification,
        },
      });
      classification =
        classificationRaw?.data?.classification?.classificationCode;
    }
    const roidData = [
      '',
      selectorCursor?.indexType
        ? selectorCursor?.indexType >= 0
          ? generateRoidPadding(selectorCursor?.indexType, 4)
          : ''
        : '',
      selectorCursor?.indexExpedition
        ? selectorCursor?.indexExpedition >= 0
          ? generateRoidPadding(selectorCursor?.indexExpedition, 7)
          : ''
        : '',
      classification ? generateRoidPadding(classification, 2) : '',
    ];
    setExpedientDefault(
      selectorCursor?.indexExpedition !== undefined &&
        selectorCursor?.indexExpedition >= 0
        ? selectorCursor.indexExpedition
        : undefined
    );
    setIndexDefault(
      selectorCursor?.indexType !== undefined && selectorCursor?.indexType >= 0
        ? selectorCursor.indexType
        : undefined
    );
  }, [selectorCursor]);

  useEffect(() => {
    callbackSetRoid();
  }, [selectorCursor]);

  useEffect(() => {
    isLanscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLanscapeTablet]);

  // Columnas ocultas por breakpoint — comentado hasta que el cliente lo requiera
  // useLayoutEffect(() => {
  //   setColumnVisibility(
  //     isPortraitTablet
  //       ? {
  //         state: false,
  //         description: false,
  //         hour: false,
  //         groupInter: false,
  //         media: false,
  //         sendStatus: false,
  //       }
  //       : isLanscapeTablet
  //         ? {
  //           hour: false,
  //           media: false,
  //           description: false,
  //         }
  //         : {}
  //   );
  // }, [isPortraitTablet, isLanscapeTablet]);

  useEffect(() => {
    setColumnVisibility(prev => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
    }));
  }, [showPredefinedFilter]);

  useEffect(() => {
    const concessions = data?.filtersData?.data?.concessions;
    setColumnVisibility(prev => ({
      ...prev,
      concession: !concessions || concessions.length > 1,
    }));
  }, [data?.filtersData?.data?.concessions]);

  const columnHelper = createColumnHelper<dataTable>();
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);

  // Ordenación server-side: campo activo y dirección derivados del cursor Redux
  const activeSortField = selectorCursor?.sortField ?? DEFAULT_SORT_FIELD;
  const activeSortColumnId =
    (Object.keys(SORTABLE_COLUMNS) as (keyof typeof SORTABLE_COLUMNS)[]).find(
      (k) => SORTABLE_COLUMNS[k] === activeSortField,
    ) ?? 'registerInitDate';
  const sortDirection: 'asc' | 'desc' =
    selectorCursor?.orderBy === 'asc' ? 'asc' : 'desc';

  const onSortColumn = useCallback(
    (sortField: string) => {
      const isActive = activeSortField === sortField;
      const nextOrderBy: 'asc' | 'desc' =
        isActive && sortDirection === 'desc' ? 'asc' : 'desc';
      // Canonicalización: registerInitDate + desc viaja como sortField: undefined EXPLÍCITO
      const isDefault =
        sortField === DEFAULT_SORT_FIELD && nextOrderBy === 'desc';
      const dataCursor = {
        sortField: isDefault ? undefined : sortField,
        orderBy: nextOrderBy,
        cursor: '',
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setCurrentServerPage(1);
      onReFetchData?.();
    },
    [activeSortField, sortDirection, dispatch, tableId, setCurrentServerPage, onReFetchData],
  );

  // Helper que evita la duplicación en las columnas ordenables.
  const sortableHeader = useCallback(
    (columnId: keyof typeof SORTABLE_COLUMNS) => () => (
      <SortableHeader
        label={translatedColumnNames[columnId as keyof typeof translatedColumnNames]?.label ?? columnId}
        active={activeSortColumnId === columnId}
        direction={sortDirection}
        onToggle={() => onSortColumn(SORTABLE_COLUMNS[columnId])}
      />
    ),
    [translatedColumnNames, activeSortColumnId, sortDirection, onSortColumn],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        cell: ({ row }) => (
          <InputRadioContainer
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <InputRadioStyled
              name="inputRadio"
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </InputRadioContainer>
        ),
      }),
      // Banderitas (spec 2026-10-22 § 8.2): justo detrás de la selección y
      // solo si la pantalla la pide.
      ...(showFlagColumn
        ? [
            createFlagColumn<dataTable>(
              getFlagOrderId),
          ]
        : []),
      columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
        header: sortableHeader('state'),
        cell: (info) => (
          <SpecialTag sizeTag={sizeTag} variant={info.getValue() as variant} />
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.roid.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {formatRoidForDisplay(info.getValue())}
          </TextCell>
        ),
        filterFn: 'roidFilter' as FilterFnOption<dataTable>, //Indicate the filter function to be used for this column
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerInitDate.id),
        {
          header: sortableHeader('registerInitDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
          filterFn: 'dateFilter' as FilterFnOption<dataTable>,
        }
      ),

      columnHelper.accessor(getColumnId(translatedColumnNames.groupInter.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.groupInter.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.media.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.media.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.sendStatus.id), {
        header: sortableHeader('sendStatus'),
        cell: (info) =>
          info.getValue() === CommunicationSendStatus.SENT ? (
            <FigureGreen>
              <GreenArrowStyled />
            </FigureGreen>
          ) : (
            <FigureRed>
              <RedArrowStyled />
            </FigureRed>
          ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.concession.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.concession.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.predefinedOrder.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.predefinedOrder.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.road.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
        header: sortableHeader('pkInit'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
        header: sortableHeader('pkEnd'),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.user.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.user.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {(info.getValue() as string) || ''}
          </TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.description.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.description.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      // Fase 4 nav Trabajo/Revisión (2026-07-12) — columna "Origen"
      // (containerType traducido con la label EXISTENTE `common:orderTypes.*`,
      // servido directo por el backend — ver gql/fragments/communications.ts).
      // Oculta por defecto en pestañas contextuales vía
      // `columnVisibility.origin` (gating por tableId).
      columnHelper.accessor(getColumnId(translatedColumnNames.containerType.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.containerType.label}
          </TextHeader>
        ),
        cell: (info) => {
          const value = info.getValue() as string | undefined;
          return (
            <TextCell size={'S'}>
              {value ? t(`common:orderTypes.${value}`) : ''}
            </TextCell>
          );
        },
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.creationDate.id),
        {
          header: sortableHeader('creationDate'),
          cell: (info) => (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
        }
      ),
    ],
    [
      translatedColumnNames,
      t,
      sortableHeader,
      sizeTag,
      showFlagColumn,
      getFlagOrderId,
    ]
  );

  // Tarea #11 — Sincronizar TanStack pageSize con `takePagination` (server).
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize: takePagination,
    }),
    [pageIndex, takePagination]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll,
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
    },
    filterFns,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true, //enable row selection for all rows
    enableMultiRowSelection: false,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  const groupInterlocutorOptions = useCallback(() => {
    return data?.filtersData?.data?.interlocutorGroups?.map(
      (interlocutor: { name: string; id: string }) => ({
        label: interlocutor.name,
        value: interlocutor.id,
      })
    );
  }, [data?.filtersData?.data?.interlocutorGroups]);

  const concessionOptions = useCallback(() => {
    return data?.filtersData?.data?.concessions?.map((concession) => ({
      label: concession.name,
      value: concession.id,
      roadIDs: concession.roadIDs,
    }));
  }, [data?.filtersData?.data?.concessions]);

  const classificationOptions = useCallback(() => {
    return data?.filtersData?.data?.classifications
      ?.slice()
      ?.sort((a, b) => (a.classificationCode ?? 0) - (b.classificationCode ?? 0))
      ?.map((classification) => ({
        label: `${classification.classificationCode} - ${classification.name}`,
        value: classification.id,
      }));
  }, [data?.filtersData?.data?.classifications]);

  const predefinedOrderOptions = useMemo(() => {
    return data?.filtersData?.data?.predefinedOrders?.map(
      (predefined: { id: string; name: string }) => ({
        label: predefined.name,
        value: predefined.id,
      })
    );
  }, [data?.filtersData?.data?.predefinedOrders]);

  // User options for filtering (multi-select). Reusa la lógica de Multimedia:
  // solo usuarios activos con rol {SuperAdmin, Coex, Tablet}. Plan 2 Tarea 15.
  //
  // Plan Task 8: cuando `includeInactive=false` pero el cursor ya tiene
  // userIds seleccionados que NO están en la base activa (porque están
  // inactivos), se mergean como missing options para que los chips
  // sigan renderizando (edge case 7.1 del spec).
  const userOptions = useMemo(() => {
    const base = filterAndSortMultimediaUsers(
      data?.filtersData?.data?.users ?? [],
      selectorCursor?.includeInactive ?? false,
    );
    const selectedIds = selectorCursor?.userIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id),
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filtersData?.data?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter((u: unknown): u is { id?: string; name?: string; surName?: string; isActive?: boolean } => Boolean(u))
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id ?? '',
      }));
    return [...base, ...missingOptions];
  }, [
    data?.filtersData?.data?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.userIds,
  ]);

  // Team options for filtering (multi-select). Derives from filtersData.data.teams
  // which comes from GET_SELECTORS_DATA (already includes `teams` field).
  const teamOptions = useMemo(
    () =>
      data?.filtersData?.data?.teams?.map((team) => ({
        label: team.name ?? '',
        value: team.id,
      })) ?? [],
    [data?.filtersData?.data?.teams],
  );

  const onChangeSelectedTeamIds = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by
      // `keyArgs: [..., 'teamIds']` produces the same key for the same
      // selection regardless of click order.
      const sorted = [...values].sort();
      const nextTeamIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        teamIds: nextTeamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, teamIds: nextTeamIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.roadId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
      dispatch,
      tableId,
    ]
  );

  //I select the column and we pass the filter with the input values
  const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
    async (value) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        indexType: selectorData.indexType ?? selectorCursor.indexType,
        indexExpedition: value,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexExpedition = value;
        return newData;
      });
      setExpedientDefault(value);
    },
    [
      selectorCursor,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.states,
      selectorData.indexType,
      selectorData.classification,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
    ]
  );

  const onChangeInputIndex = useCallback<onChangeInputIndexTypes>(
    async (value) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        indexType: value,
        indexExpedition:
          selectorData.indexExpedition ?? selectorCursor.indexExpedition,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexType = value;
        return newData;
      });
      setIndexDefault(value);
    },
    [
      selectorCursor,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.states,
      selectorData.indexExpedition,
      selectorData.classification,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
    ]
  );

  const onChangePKInit = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkInitKm: km, pkInitMeter: meter }));
    },
    []
  );

  const onChangePKEnd = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkEndKm: km, pkEndMeter: meter }));
    },
    []
  );

  /**
   * *Function for filtering date ranges
   * @param range is a range of dates ({from, to})
   */
  const onChangeDateInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: fromDateIso,
        toDate: toDateIso,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedPartnerGroup = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId: option.value,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData.indexExpedition ?? selectorCursor.indexExpedition,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.interlocutorGroupId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.states,
      selectorData.indexType,
      selectorData.indexExpedition,
      selectorData.classification,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
    ]
  );

  const onChangeSelectedClassification = useCallback(
    (value: OptionSelected) => {
      let isSame = false;
      if (selectorData.classification !== value.value) {
        setSelectorData({
          ...selectorData,
          classification: value.value,
        });

        const setCursorData = {
          ...selectorCursor,
          classification: value.value,
        };

        dispatch(setCursor(tableId as string, setCursorData));
      } else {
        isSame = true;
      }
      return isSame;
    },
    [selectorData, selectorCursor, dispatch, tableId]
  );

  const onChangeSelectedPredefinedOrder = useCallback(
    (value: OptionSelected) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId: value.value,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        predefinedOrderId: value.value,
      }));
    },
    [selectorData, selectorCursor, dispatch, tableId]
  );

  const onChangeSelectedConcession = useCallback(
    async (option: option) => {
      const { data } = await client.query({
        query: GET_ROADS_BY_IDS,
        variables: {
          ids: option.roadIDs,
        },
      });
      setRoadOptions(
        data?.readRoads?.map((road: { id: string; name: string }) => ({
          value: road.id,
          label: road.name,
        }))
      );
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: undefined,
        concessionId: option.value,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        newData.roadId = undefined;
        return newData;
      });

      inputRoadRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
    ]
  );

  // Auto-select the only concession if there's only one available and filter roads
  useEffect(() => {
    const options = concessionOptions();
    if (
      options &&
      options.length === 1 &&
      !selectorCursor.concessionId &&
      !hasAutoSelectedConcession.current
    ) {
      const onlyConcession = options[0];
      hasAutoSelectedConcession.current = true;
      // Call the callback to filter roads by concession
      onChangeSelectedConcession(onlyConcession);
    }
  }, [concessionOptions, selectorCursor.concessionId, onChangeSelectedConcession]);

  // Filter roads if concessionId is already in cursor (on mount or navigation)
  // Also re-filter when data changes (after revalidate)
  useEffect(() => {
    const filterRoadsByConcessionOnMount = async () => {
      const options = concessionOptions();
      if (
        selectorCursor.concessionId &&
        options &&
        options.length > 0
      ) {
        const selectedConcession = options.find(
          (c) => c.value === selectorCursor.concessionId
        );
        if (selectedConcession && selectedConcession.roadIDs) {
          const { data } = await client.query({
            query: GET_ROADS_BY_IDS,
            variables: {
              ids: selectedConcession.roadIDs,
            },
          });

          const filteredRoads = data?.readRoads?.map(
            (road: { id: string; name: string }) => ({
              value: road.id,
              label: road.name,
            })
          );

          setRoadOptions(filteredRoads || []);
        }
      } else if (!selectorCursor.concessionId) {
        // If no concession is selected, restore all roads
        setRoadOptions(initialRoadOptions);
      }
    };

    filterRoadsByConcessionOnMount();
  }, [selectorCursor.concessionId, concessionOptions, initialRoadOptions]);

  const onChangeSelectedRoad = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: option.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.roadId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
    ]
  );

  const onChangeSelectedStates = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'states']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextStates = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: nextStates,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, states: nextStates }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.roadId,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
      dispatch,
      tableId,
    ]
  );

  const onChangeSelectedUserIds = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by
      // `keyArgs: [..., 'userIds']` produces the same key for the same
      // selection regardless of click order.
      const sorted = [...values].sort();
      const nextUserIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: nextUserIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, userIds: nextUserIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.roadId,
      selectorData?.states,
      selectorData?.containerTypes,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
      dispatch,
      tableId,
    ]
  );

  // Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen" (containerType:
  // generic|campaign|surveillanceRoute). Mismo patrón que
  // `onChangeSelectedUserIds` — canonicaliza con sort estable. Solo se monta
  // en la tabla GLOBAL (ver gating en el render).
  const onChangeSelectedContainerTypes = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextContainerTypes = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: nextContainerTypes,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, containerTypes: nextContainerTypes }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.roadId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
      dispatch,
      tableId,
    ]
  );

  // «Solo marcadas» (spec 2026-10-22 § 8.3). Solo guarda en el cursor —el
  // reducer hace merge, así que basta con su clave— y la búsqueda la lanza
  // Buscar. Desmarcado es `undefined`, nunca `false`: así la caché de Apollo no
  // se parte en dos claves para «sin filtro».
  const onChangeOnlyFlagged = useCallback(
    (next: boolean) => {
      dispatch(
        setCursor(tableId as string, { onlyFlagged: next ? true : undefined })
      );
    },
    [dispatch, tableId]
  );

  // Plan Task 8: toggle "Incluir inactivos en Creador".
  // Solo expone usuarios desactivados en el dropdown Creador; NO filtra
  // resultados del listado. Persiste en el cursor (Redux) y dispara
  // onReFetchData via useEffect aguas abajo.
  const onChangeIncludeInactive = useCallback(
    (next: boolean) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: next,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        includeInactive: next,
      }));
    },
    [selectorCursor, selectorData, dispatch, tableId],
  );

  const onClearValue = useCallback(() => {
    inputListGroupRef.current?.clearSelected();
    inputDateRef.current?.clearSelected();
    inputExpedientRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    inputRoadRef.current?.clearSelected();
    inputListClassificationRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputUserRef.current?.clearSelected();
    inputContainerTypeRef.current?.clearSelected();
    inputTeamRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const options = concessionOptions();
    const hasOnlyOneConcession = options && options.length === 1;
    const onlyConcessionId = hasOnlyOneConcession ? options[0].value : undefined;

    // If there's only one concession, keep it selected
    // The useEffect will handle re-filtering roads automatically when concessionId changes
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
      hasAutoSelectedConcession.current = false;
    }

    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: undefined,
      indexExpedition: undefined,
      roadId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      interlocutorGroupId: undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      orderBy: 'desc',
      sortField: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      // Explícito: el reducer hace merge y omitirlo conservaría el filtro.
      onlyFlagged: undefined,
    };
    dispatch(setCursor(tableId, dataCursor));
    setCurrentServerPage(1);
    // Fix: Create new object instead of mutating to trigger useEffect
    setSelectorCursor((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      roadId: undefined,
      interlocutorGroupId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      sortField: undefined,
      orderBy: undefined,
      onlyFlagged: undefined,
    }));
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      roadId: undefined,
      interlocutorGroupId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      predefinedOrderId: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      containerTypes: undefined,
      teamIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
    }));
    onReFetchData?.();
    // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
  }, [concessionOptions, initialRoadOptions, onChangeInputExpedient, onChangeInputIndex, takePagination]);

  const handleClickRow = (row: Row<dataTable>) => {
    const val: boolean = row.getIsSelected();
    if (!val) {
      setRowState(row.original.id || '');
    } else {
      setRowState('');
    }
  };

  // Sync rowSelection → rowState for direct radio button clicks (stopPropagation prevents onSingleTap)
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter((key) => (rowSelection as Record<string, boolean>)[key]);
    setRowState(selectedIds.length > 0 ? selectedIds[0] : '');
  }, [rowSelection, setRowState]);

  // Columnas que ofrece el desplegable «Columnas»: todas menos la de selección
  // (la primera) y las que no se pueden ocultar (la bandera, § 8.2). Sin
  // bandera queda igual que el `.slice(1)` de siempre.
  const hideableColumns = table
    .getAllColumns()
    .slice(1)
    .filter((column) => column.getCanHide());

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  // commonColumns definition removed

  // commonLabels definition removed

  const fetchCommunicationID = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        cursor: id,
        roadId: selectorData?.roadId ?? selectorCursor?.roadId,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        interlocutorGroupId:
          selectorData?.interlocutorGroupId ??
          selectorCursor?.interlocutorGroupId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        sortField: selectorCursor?.sortField,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        states: selectorData.states ?? selectorCursor.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };

      if (take > 0) {
        const lastCommunication = dataAll[dataAll.length - 1].communicationID;

        if (lastCommunication) {
          dispatch(setCursor(tableId, dataSelector));
        }
      } else {
        const firstCommunication = dataAll[0].communicationID;

        if (firstCommunication) {
          dispatch(setCursor(tableId, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.roadId,
      selectorData?.concessionId,
      selectorData?.interlocutorGroupId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.predefinedOrderId,
      dataAll,
      takePagination,
    ]
  );

  const nextPagination = useCallback(async () => {
    // Clamp defensivo contra bug off-by-one (backend/cache hasNextPage true en última real).
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination),
    );
    if (
      dataAll.length >= 1 &&
      data.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchCommunicationID(
        dataAll[dataAll.length - 1].communicationID as string
      );

      onReFetchPagination();
    }
  }, [dataAll, data, fetchCommunicationID, takePagination, currentServerPage]);

  const backPagination = useCallback(async () => {
    if (dataAll.length >= 1 && data.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchCommunicationID(
        dataAll[0].communicationID as string,
        -takePagination
      );

      onReFetchPagination();
    }
  }, [data, dataAll, fetchCommunicationID, takePagination]);

  const handleSearch = async () => {
    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: selectorData?.indexType ?? selectorCursor?.indexType,
      indexExpedition:
        selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
      roadId: selectorData?.roadId ?? selectorCursor?.roadId,
      concessionId: selectorData?.concessionId ?? selectorCursor?.concessionId,
      interlocutorGroupId:
        selectorData?.interlocutorGroupId ??
        selectorCursor?.interlocutorGroupId,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      classification:
        selectorData?.classification ?? selectorCursor?.classification,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      states: selectorData?.states ?? selectorCursor?.states,
      userIds: selectorData?.userIds ?? selectorCursor?.userIds,
      containerTypes: selectorData?.containerTypes ?? selectorCursor?.containerTypes,
      teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
      includeInactive: selectorData?.includeInactive ?? selectorCursor?.includeInactive,
      pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
      pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
      pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
      pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      description:
        (selectorData?.description ?? selectorCursor?.description) || undefined,
    };
    dispatch(setCursor(tableId as string, dataCursor));
    setCurrentServerPage(1);

    onReFetchData?.();
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(filterContainerRef, handleSearch, onClearValue, isLoadingData as boolean);

  return (
    <ContainerStyled>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoadingData}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'} size={'M'}>
            {titleText}
          </Title>
          {isLanscapeTablet && (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters} />
            </ShowFiltersButton>
          )}
        </SubContainerHead>
        {showFilters && (
          <SubContainerFilters ref={filterContainerRef}>
            <InputExpedient
              label={t('headers.expedient')}
              disabled={isLoadingData}
              expedient={expedientDefault}
              onChangeInputExpedient={onChangeInputExpedient}
              ref={inputExpedientRef}
            />

            <InputIndex
              label={t('headers.index')}
              disabled={isLoadingData}
              index={indexDefault}
              onChangeInputIndex={onChangeInputIndex}
              ref={inputIndexRef}
            />
            <InputPK
              label={t('tables:headers.pkInit')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKInit}
              ref={inputPKInitRef}
            />
            <InputPK
              label={t('tables:headers.pkEnd')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKEnd}
              ref={inputPKEndRef}
            />
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada). */}
            <InputText
              label={t('tables:headers.description')}
              typeInput="normal"
              inputSize="S"
              placeholder={t('common:placeholders.writeHere')}
              disabled={isLoadingData}
              value={
                selectorData?.description ?? selectorCursor?.description ?? ''
              }
              onChange={(e) =>
                setSelectorData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <InputCalendarStyled
              ref={inputDateRef}
              label={t('tables:filters.dateRangeFilter')}
              name={'data'}
              dateRange={
                selectorCursor?.fromDate && selectorCursor?.toDate
                  ? {
                    from: parseISO(selectorCursor?.fromDate),
                    to: parseISO(selectorCursor?.toDate),
                  }
                  : undefined
              }
              onChangeInput={onChangeDateInput}
              disabled={isLoadingData}
            />
            {concessionOptions() && (concessionOptions()?.length ?? 0) > 1 && (
              <DropDownInput
                inputSize="XS"
                ref={inputListConRef}
                labelText={t('tables:headers.concession')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={selectorCursor?.concessionId ?? ''}
                options={concessionOptions() as Options}
                onChangeSelected={onChangeSelectedConcession as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            <DropDownInput
              inputSize="XS"
              ref={inputRoadRef}
              labelText={t('tables:headers.road')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor?.roadId ?? ''}
              options={roadOptions as Options}
              onChangeSelected={onChangeSelectedRoad as onChangeValue}
              isDisabled={isLoadingData}
            />
            <SearchableDropdownStyled
              inputSize="L"
              ref={inputListClassificationRef}
              labelText={t('tables:headers.classification')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Default'}
              defaultValue={selectorCursor?.classification ?? ''}
              selectedOption={selectorCursor?.classification ?? undefined}
              options={classificationOptions() as Options}
              onChangeSelected={
                onChangeSelectedClassification as onChangeValue
              }
              isDisabled={isLoadingData}
            />
            {showPredefinedFilter && predefinedOrderOptions && predefinedOrderOptions.length > 0 && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListPredefinedRef}
                labelText={t('forms:workOrder.predefined')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                options={predefinedOrderOptions as Options}
                onChangeSelected={onChangeSelectedPredefinedOrder as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            {!isLanscapeTablet && (
              <DropDownInput
                inputSize="M"
                ref={inputListGroupRef}
                labelText={t('tables:headers.interlocutorGroup')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.interlocutorGroupId ?? ''}
                options={groupInterlocutorOptions() as Options}
                onChangeSelected={onChangeSelectedPartnerGroup as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            <MultiSelectDropdownStyled
              inputSize="S"
              ref={inputStateRef}
              labelText={t('tables:headers.status')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              selectedValues={selectorCursor?.states ?? []}
              options={roleActive ? getStateOptionsByRoleAndEntityType(t, roleActive as Role, TABLE_ENTITY_TYPES[tableId as string], listingFiltersByRole(tableId)) : []}
              onChangeSelected={onChangeSelectedStates}
              isDisabled={isLoadingData}
            />

            {userOptions && userOptions.length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputUserRef}
                labelText={t('common:labels.creator')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.userIds ?? []}
                options={userOptions}
                onChangeSelected={onChangeSelectedUserIds}
                isDisabled={isLoadingData}
              />
            )}

            {/* Fase 4 nav Trabajo/Revisión (2026-07-12) — filtro "Origen".
                Oculto en pestañas contextuales (campaña/RV). */}
            {!isContainerScopedTable && (
              <MultiSelectDropdownStyled
                inputSize="S"
                ref={inputContainerTypeRef}
                labelText={t('common:tables.headers.origin')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.containerTypes ?? []}
                options={getContainerTypeOptions(t)}
                onChangeSelected={onChangeSelectedContainerTypes}
                isDisabled={isLoadingData}
              />
            )}

            {teamOptions && teamOptions.length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputTeamRef}
                labelText={t('forms:workOrder.team')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                selectedValues={selectorCursor?.teamIds ?? []}
                options={teamOptions}
                onChangeSelected={onChangeSelectedTeamIds}
                isDisabled={isLoadingData}
              />
            )}

            <ContainerButtons>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columnNames={Object.fromEntries(hideableColumns.map(c => [c.id, translatedColumnNames[c.id as keyof typeof translatedColumnNames]?.label || c.id]))}
                columns={hideableColumns}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => { setCurrentServerPage(1); onReFetchData?.(); }}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtons>
            <IncludeInactiveToggle
              checked={selectorCursor?.includeInactive ?? false}
              onChange={onChangeIncludeInactive}
              label={t('common:filters.includeInactiveInCreator')}
              isDisabled={isLoadingData}
            />
            {showOnlyFlaggedFilter && (
              <IncludeInactiveToggle
                checked={Boolean(selectorCursor?.onlyFlagged)}
                onChange={onChangeOnlyFlagged}
                label={flagScopeTexts.onlyFlagged}
                isDisabled={isLoadingData}
              />
            )}
          </SubContainerFilters>
        )}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoadingData}>
        <Table {...props}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    {...exportIgnoreProps(header.column.id)}
                    aria-sort={
                      activeSortColumnId === header.id
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <DoubleTapRow
                  key={row.id}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                  isDisabled={isLoadingData as boolean}
                  onSingleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    // La pantalla sabe adónde se vuelve: si lo dice, manda ella.
                    if (onRowOpen && row.original?.id) {
                      onRowOpen(row.original.id);
                      return;
                    }
                    if (row.original?.id) {
                      const path = getDoubleTapNavigationPath
                        ? getDoubleTapNavigationPath(row.original.id)
                        : `/work-orders/communications/${row.original.id}`;
                      navigate(path);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      column={cell.column.id as columnID}
                      isFigure={
                        cell.column.id === translatedColumnNames.sendStatus.id
                      }
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                      {...exportIgnoreProps(cell.column.id)}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
      <PaginationButtons
        pageCount={Math.max(1, Math.ceil((data?.totalCount ?? 0) / takePagination))}
        currentPage={currentServerPage}
        totalCount={data?.totalCount ?? 0}
        nextPage={nextPagination}
        previousPage={backPagination}
        isDisabled={isLoadingData}
        // Tarea #11: gates aplicados en `useServerPageSize` — en tablet o
        // contexto modal (DUPLICATE_COMMUNICATION), los 3 props van como
        // undefined y el selector no se renderiza.
        {...paginationProps}
      />
    </ContainerStyled>
  );
};
